import { ReactNode } from 'react';
import styles from './styles.module.css';
import { QueueableError } from '../../../types/errors';
import Button from '../Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../../redux/store';
import { resetError } from '../../../redux/errorSlice';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { isApiErrorDataObject } from '../../../services/isApiErrorDataObject';
import { resetTokens } from '../../../redux/tokenSlice';

type ErrorModalContent = {
  title: string;
  content: string | ReactNode;
  buttonText: string;
  onClose: () => void;
};
const useModalErrorContent = (e: QueueableError): ErrorModalContent => {
  const { reset } = useModalQueue();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const generalContent = {
    title: 'エラーが発生しました',
    content: (
      <>
        通信時にエラーが発生しました。
        <br />
        しばらく待ってから操作をやり直してください。
      </>
    ),
    buttonText: '確認',
    onClose: reset,
  };
  if (!e || !isApiErrorDataObject(e.data)) {
    return generalContent;
  }

  if (e.data.code === 400) {
    return {
      title: '入力項目にエラーがあります',
      content: (
        <div>
          <div>{e.data.message}</div>
          <div>
            {(e.data.errors || []).map((ve, i) => (
              <div key={i}>
                {ve.messages.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ),
      buttonText: '確認',
      onClose: reset,
    };
  }

  if (e.data.code === 401) {
    return {
      title: 'ログイン有効期限が切れました',
      content: 'ログイン画面に戻り、再度認証してください。',
      buttonText: 'ログイン画面に戻る',
      onClose: () => {
        reset();
        dispatch(resetTokens());
        navigate('/login');
      },
    };
  }

  if (e.data.code === 403) {
    return {
      title: '権限がありません',
      content: (
        <>
          この操作を行うための権限がありません。
          <br />
          管理者にお問い合わせください。
        </>
      ),
      buttonText: '確認',
      onClose: reset,
    };
  }

  // reason の設定がない場合汎用文言で処理する
  // cf: https://project-fenrir.slack.com/archives/C04SCNQFCAH/p1702101350709629?thread_ts=1702086304.281029&cid=C04SCNQFCAH
  if (e.data.code === 409 || e.data.reason) {
    return {
      title: e.data.message,
      content: e.data.reason,
      buttonText: '確認',
      onClose: reset,
    };
  }

  return generalContent;
};

const ErrorModal = () => {
  const { currentError: error } = useModalQueue();
  const closeHandler: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    content.onClose();
  };
  const content = useModalErrorContent(error);

  if (!error) return null;
  return (
    <div className={styles.backdrop} onClick={closeHandler}>
      <div
        className={styles['modal-container']}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <button onClick={closeHandler} className={styles.close}>
          <FontAwesomeIcon icon={faXmark} className={styles.icon} />
        </button>
        <div className={styles.content}>
          <div className={styles.errorTitle}>{content.title}</div>
          <div className={styles.message}>{content.content}</div>
          <div className={classNames(styles.button, styles.spOnly)}>
            <Button variants={['fit']} onClick={closeHandler}>
              {content.buttonText}
            </Button>
          </div>
          <div className={classNames(styles.button, styles.pcOnly)}>
            <Button variants={['large']} onClick={closeHandler}>
              {content.buttonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ErrorModal;

const useModalQueue = () => {
  const dispatch = useDispatch<AppDispatch>();
  const errorQueue = useSelector((state: AppState) => state.error.errorQueue);
  const currentError: QueueableError | undefined = errorQueue[0];
  const reset = () => {
    dispatch(resetError());
  };
  return {
    currentError,
    reset,
  };
};
